import { useState, useMemo } from 'react';
import { ArrowUpRight, ArrowDownRight, Clock, AlertCircle, BarChart2, HandCoins, Wallet, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { KpiCard } from './shared/KpiCard';
import { CashflowChart } from './shared/CashflowChart';
import { MovimientosTable } from './shared/MovimientosTable';
import { DateRangeFilter } from './shared/DateRangeFilter';
import { DateRangeSelector } from './shared/DateRangeSelector';
import { DistribucionChart, INGRESOS_PALETTE, EGRESOS_PALETTE } from './shared/DistribucionChart';
import { CuentasCards } from './shared/CuentasCards';
import { SectionCard } from './shared/SectionCard';
import { CONTABILIDAD_ROUTES } from '../constants/routes';
import { SECTION_LABEL_CLASS } from '../constants/ui';
import { aggregateByCategoria } from '../utils/aggregations';
import { matchesDateRange, shouldShowUtilidadALiquidar } from '../utils/dateRange';
import { useKpis } from '../hooks/useKpis';
import { useMovimientos } from '../hooks/useMovimientos';
import { useChartData } from '../hooks/useChartData';
import { useCajas } from '../hooks/useCajas';
import { useDateRangeFilter, buildMonthRange, buildYearRange } from '../hooks/useDateRangeFilter';
import type { Moneda } from '../types';
import { cn } from '@/lib/utils';

export function ResumenPage() {
  const [monedaChart, setMonedaChart] = useState<Moneda>('ARS');
  // Arranca filtrado al mes actual (no "todos").
  const { range, activeMonth, selectMonth, selectRange, selectAll } = useDateRangeFilter(
    buildMonthRange(new Date().getMonth()),
  );

  const { kpis, isLoading: isLoadingKpis } = useKpis(range);
  const { movimientos, isLoading: isLoadingMovimientos } = useMovimientos();
  // El Flujo de Caja siempre muestra el año completo, sin importar el filtro de la página.
  const rangoAnioCompleto = useMemo(() => buildYearRange(new Date().getFullYear()), []);
  const { chartData, chartDataUSD } = useChartData(rangoAnioCompleto);
  const { cajas } = useCajas();

  const isLoading = isLoadingKpis || isLoadingMovimientos;

  // Filtrar movimientos client-side por rango de fechas.
  // Nota: Cobros Pendientes y Egresos a Vencer (dentro de `kpis`) son saldos
  // actuales sin fecha propia, por lo que el backend siempre los devuelve
  // como total global, sin importar el filtro de fecha.
  const movimientosFiltrados = useMemo(
    () => movimientos.filter((m) => matchesDateRange(m.fecha, range)),
    [movimientos, range],
  );

  if (isLoading || !kpis) {
    return <div className="p-5 text-sm text-muted-foreground">Cargando...</div>;
  }

  const kpisARS = kpis.ARS;
  const kpisUSD = kpis.USD;
  const cajaTotalARS = cajas.filter((c) => c.moneda === 'ARS').reduce((sum, c) => sum + c.saldo, 0);
  const cajaTotalUSD = cajas.filter((c) => c.moneda === 'USD').reduce((sum, c) => sum + c.saldo, 0);
  const recentMovimientos = movimientosFiltrados.slice(0, 8);
  const activeChartData = monedaChart === 'ARS' ? chartData : chartDataUSD;
  const distribucionIngresos = aggregateByCategoria(movimientosFiltrados, 'Ingreso', 'ARS');
  const distribucionEgresos = aggregateByCategoria(movimientosFiltrados, 'Egreso', 'ARS');

  // "Utilidad a Liquidar" solo tiene sentido del mes actual en adelante;
  // en períodos pasados esa utilidad ya quedó total o parcialmente liquidada.
  // En "Todos" (año completo) se mezclan meses pasados y el actual, así que
  // también se muestra únicamente la Liquidada.
  const showALiquidar = activeMonth !== 'todos' && shouldShowUtilidadALiquidar(range);

  // "Caja Total" representa el saldo real de hoy: solo tiene sentido en el mes en curso.
  const showCajaTotal = activeMonth === new Date().getMonth();

  const monedaToggle = (
    <div className="flex items-center gap-1 bg-gray-100 rounded-full px-1.5 py-1 border border-gray-200">
      {(['ARS', 'USD'] as Moneda[]).map((m) => (
        <button
          key={m}
          onClick={() => setMonedaChart(m)}
          className={cn(
            'px-3 py-0.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap',
            monedaChart === m
              ? 'bg-white text-primary shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {m}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-2 sm:p-5 bg-slate-50 min-h-full">

      {/* Toolbar */}
      <div className="flex justify-end items-center gap-3 flex-wrap mb-5">
        <DateRangeFilter
          activeMonth={activeMonth}
          onSelectMonth={(m) => m === 'todos' ? selectAll() : selectMonth(m as number)}
        />
        <DateRangeSelector value={range} onChange={(r) => r ? selectRange(r) : selectAll()} />
      </div>

      {/* Dashboard grid — 12 columns */}
      <div className="grid grid-cols-12 gap-5">

        {/* ── Fila 1: KPIs top ── */}
        {/* flex-wrap: cada card tiene un ancho base y crece para ocupar el espacio sobrante
            de su fila (a diferencia de grid, acá las filas parciales no dejan huecos) */}
        <div className="col-span-12 flex flex-wrap gap-5">
          <KpiCard
            title="Ingresos"
            value={kpisARS.ingresos}
            moneda="ARS"
            secondary={{ value: kpisUSD.ingresos, moneda: 'USD' }}
            icon={ArrowUpRight}
            iconBg="bg-gray-100"
            iconColor="text-gray-500"
            className="grow shrink min-w-0 basis-[260px]"
          />

          <KpiCard
            title="Egresos"
            value={kpisARS.egresos}
            moneda="ARS"
            secondary={{ value: kpisUSD.egresos, moneda: 'USD' }}
            icon={ArrowDownRight}
            iconBg="bg-gray-100"
            iconColor="text-gray-500"
            className="grow shrink min-w-0 basis-[260px]"
          />

          {showALiquidar && (
            <KpiCard
              title="Utilidad a Liquidar"
              value={kpisARS.disponible}
              moneda="ARS"
              secondary={{ value: kpisUSD.disponible, moneda: 'USD' }}
              icon={BarChart2}
              iconBg="bg-primary/20"
              iconColor="text-primary"
              className="grow shrink min-w-0 basis-[260px] border-2 border-primary bg-primary/5"
            />
          )}

          <KpiCard
            title="Utilidad Liquidada"
            value={kpisARS.distribuido}
            moneda="ARS"
            secondary={{ value: kpisUSD.distribuido, moneda: 'USD' }}
            icon={HandCoins}
            iconBg="bg-primary/20"
            iconColor="text-primary"
            className="grow shrink min-w-0 basis-[260px] border-2 border-primary bg-primary/5"
          />

          {showCajaTotal && (
            <KpiCard
              title="Caja Total"
              value={cajaTotalARS}
              moneda="ARS"
              secondary={{ value: cajaTotalUSD, moneda: 'USD' }}
              icon={Wallet}
              className='grow shrink min-w-0 basis-[260px]'
            />
          )}
          <KpiCard
            title="Cobros Pendientes"
            value={kpisARS.cobrosPendientes}
            moneda="ARS"
            secondary={{ value: kpisUSD.cobrosPendientes, moneda: 'USD' }}
            icon={Clock}
            iconBg="bg-gray-100"
            iconColor="text-gray-500"
            className='grow shrink min-w-0 basis-[260px]'
          />
          <KpiCard
            title="Egresos a Vencer"
            value={kpisARS.egresosPendientes}
            moneda="ARS"
            secondary={{ value: kpisUSD.egresosPendientes, moneda: 'USD' }}
            icon={AlertCircle}
            iconBg="bg-gray-100"
            iconColor="text-gray-500"
            className='grow shrink min-w-0 basis-[260px]'
          />
        </div>

        {/* ── Fila 2: Gráfico (8) + Donuts (4) ── */}

        {/* Columna izquierda: 2 KPIs + Chart */}
        <div className="col-span-12 xl:col-span-8 flex flex-col gap-5">

          {/* Chart Flujo de Caja */}
          <SectionCard
            title="Flujo de Caja (Año actual)"
            action={monedaToggle}
            className="flex-1 p-5 flex flex-col"
          >
            <div className="flex-1 min-h-0">
              <CashflowChart data={activeChartData} moneda={monedaChart} />
            </div>
          </SectionCard>

        </div>

        {/* Columna derecha: 2 Donuts apilados */}
        <div className="col-span-12 xl:col-span-4 flex flex-col gap-5">

          <SectionCard title="Distribución Ingresos" className="flex-1 p-4 flex flex-col">
            <DistribucionChart data={distribucionIngresos} palette={INGRESOS_PALETTE} moneda="ARS" />
          </SectionCard>

          <SectionCard title="Distribución Egresos" className="flex-1 p-4 flex flex-col">
            <DistribucionChart data={distribucionEgresos} palette={EGRESOS_PALETTE} moneda="ARS" />
          </SectionCard>

        </div>

        {/* ── Fila 3: Cajas — CuentasCards renderiza su propio wrapper flex que cubre el 100% del ancho ── */}
        <CuentasCards cajas={cajas} />

        {/* ── Fila 4: Movimientos Recientes (full width) ── */}
        <SectionCard className="col-span-12">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <p className={SECTION_LABEL_CLASS}>Movimientos Recientes</p>
            <Button variant="outline" size="sm" asChild>
              <Link to={CONTABILIDAD_ROUTES.MOVIMIENTOS}>
                Ver todos <ArrowRight className="size-3 ml-1" />
              </Link>
            </Button>
          </div>
          <div className="p-5 pt-3">
            <MovimientosTable movimientos={recentMovimientos} />
          </div>
        </SectionCard>

      </div>
    </div>
  );
}
